import { useEffect, useRef, useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { GoUpload } from "react-icons/go";
import { BsDownload } from "react-icons/bs";
import { ChevronDown } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import {
	useGetCustomTaxReliefGroup,
	useGetCustomTaxReliefGroupSearch,
	useDeleteCustomTaxReliefGroup,
} from "../../../../services/custom-tax-relief-group.service";
import {
	useGetCustomTaxRelief,
	useDeleteCustomTaxRelief,
} from "../../../../services/custom-tax-relief.service";
import { useGetEmployeeBand } from "../../../../services/employee-band.service";
import { useGetEmployee } from "../../../../services/employee.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import { EmployeeBandOrderBy } from "../../../../models/employee-band.dto";
import { CustomTaxReliefGroupDto } from "../../../../models/custom-tax-relief-group.dto";
import { CustomTaxReliefDto } from "../../../../models/custom-tax-relief.dto";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import {
	PermissionChecker,
	PERMISSIONS,
	useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";
import ActionPopover from "../../../../components/popovers/actionPopover";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import APISearchInput from "../../../../components/search/APISearchInput";
import TableFilter from "../../../../components/table/filter/TableFilter";
import UploadCustomTaxRelief from "../modal/UploadCustomTaxRelief";
import ExportCustomTaxReliefModal from "../modal/ExportCustomTaxReliefModal";
import CustomTaxReliefGroupModalForm from "../modal/CustomTaxReliefGroupModalForm";
import CustomTaxReliefModalForm from "../modal/CustomTaxReliefModalForm";
import {
	customTaxReliefGroupColumns,
	customTaxReliefGroupColumnVisibility,
	customTaxReliefColumns,
	customTaxReliefColumnVisibility,
} from "../tanstack-columns";

const CustomTaxReliefs = () => {
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();
	const { formDataValues, setFormData } = useEditFormStore();
	const [searchParams, setSearchParams] = useSearchParams();

	// ─── Tab ─────────────────────────────────────────────────────────────────
	const [activeTab, setActiveTab] = useState<"groups" | "reliefs">("groups");

	// ─── Modal state ──────────────────────────────────────────────────────────
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [openGroupModal, setOpenGroupModal] = useState(false);
	const [openReliefModal, setOpenReliefModal] = useState(false);
	const [openUploadModal, setOpenUploadModal] = useState(false);
	const [openExportModal, setOpenExportModal] = useState(false);
	const [isAddOpen, setIsAddOpen] = useState(false);
	const addDropdownRef = useRef<HTMLDivElement | null>(null);

	// ─── Column visibility ────────────────────────────────────────────────────
	const {
		columnVisibilityModel: groupColVisibility,
		setColumnVisibilityModel: setGroupColVisibility,
	} = useColumnVisibilityModel(customTaxReliefGroupColumnVisibility);

	const {
		columnVisibilityModel: reliefColVisibility,
		setColumnVisibilityModel: setReliefColVisibility,
	} = useColumnVisibilityModel(customTaxReliefColumnVisibility);

	// ─── Groups table ─────────────────────────────────────────────────────────
	const { mutate: searchGroups } = useGetCustomTaxReliefGroupSearch(currentCompany?.id as number);
	const {
		tableAPIRef: groupTableAPIRef,
		fetchedTableData: fetchedGroupData,
		tableData: groupTableData,
		isLoading: groupLoading,
		pageCount: groupPageCount,
		setTableData: setGroupTableData,
	} = useManageTableWithSearchParams<CustomTaxReliefGroupDto>({
		fetchHook: (options) =>
			useGetCustomTaxReliefGroup(currentCompany?.id as number, options),
		options: {},
	});

	// ─── Reliefs table ────────────────────────────────────────────────────────
	const {
		tableAPIRef: reliefTableAPIRef,
		fetchedTableData: fetchedReliefData,
		tableData: reliefTableData,
		isLoading: reliefLoading,
		pageCount: reliefPageCount,
		setTableData: setReliefTableData,
	} = useManageTableWithSearchParams<CustomTaxReliefDto>({
		fetchHook: useGetCustomTaxRelief,
		options: { companyId: currentCompany?.id },
	});

	// ─── Form data sources ────────────────────────────────────────────────────
	const { data: customTaxReliefGroupsList } = useGetCustomTaxReliefGroup(
		currentCompany?.id as number,
		{
			limit: APP_CONFIG.PAGE_LIMIT,
			orderBy: "createdAt:desc" as any,
		} as any,
	);
	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});
	const { data: employeeBands } = useGetEmployeeBand({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: EmployeeBandOrderBy.NAME_ASC,
	});

	// ─── Delete ───────────────────────────────────────────────────────────────
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteGroup } = useDeleteCustomTaxReliefGroup();
	const { mutate: deleteRelief } = useDeleteCustomTaxRelief();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleClose = () => setAnchorEl(null);

	const handleDeleteMethod = () => {
		setDeleting(true);
		if (activeTab === "groups") {
			deleteGroup(
				{ companyId: currentCompany?.id as number, id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		} else {
			deleteRelief(
				{ id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		}
	};

	// ─── Tab change ───────────────────────────────────────────────────────────
	const handleTabChange = (tab: "groups" | "reliefs") => {
		setActiveTab(tab);
		const updated = new URLSearchParams(searchParams);
		updated.delete("page");
		updated.delete("pageSize");
		setSearchParams(updated);
	};

	// ─── Dropdown close-on-outside-click ─────────────────────────────────────
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				addDropdownRef.current &&
				!addDropdownRef.current.contains(event.target as Node)
			) {
				setIsAddOpen(false);
			}
		};
		if (isAddOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isAddOpen]);

	// ─── Render ───────────────────────────────────────────────────────────────
	return (
		<div className="font-inter">
			{/* Toolbar */}
			<div className="flex justify-between items-center mt-4 mb-8">
				{/* Left: search / filter / columns */}
				<div className="flex items-center gap-3">
					<TableFilter
						fields={
							activeTab === "groups"
								? [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Active",
											fieldName: "active",
											fieldType: "YES_NO",
										},
									]
								: [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Amount", fieldName: "amount" },
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Employee Band",
											fieldName: "employeeBandId",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
										{
											title: "Tax Relief Group",
											fieldName: "groupId",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
										{
											title: "Active",
											fieldName: "active",
											fieldType: "YES_NO",
										},
									]
						}
					/>
					<ManageTableColumns
						columns={
							activeTab === "groups"
								? (customTaxReliefGroupColumns as any)
								: (customTaxReliefColumns as any)
						}
						defaultVisibilityModel={
							activeTab === "groups"
								? customTaxReliefGroupColumnVisibility
								: customTaxReliefColumnVisibility
						}
						visibilityModel={
							activeTab === "groups" ? groupColVisibility : reliefColVisibility
						}
						setVisibilityModel={
							activeTab === "groups"
								? setGroupColVisibility
								: setReliefColVisibility
						}
						tableApiRef={
							activeTab === "groups" ? groupTableAPIRef : reliefTableAPIRef
						}
					/>
				</div>

				{/* Right: Upload + Export + Add */}
				<div className="flex items-center gap-4 font-medium">
					{/* Upload — only on Groups tab */}
					{activeTab === "groups" && (
						<PermissionChecker
							permissionRule="OR"
							permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
						>
							<button
								onClick={() => setOpenUploadModal(true)}
								className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
							>
								<GoUpload className="mr-2 text-[17px] text-gray-500" />
								Upload Custom Tax Reliefs
							</button>
						</PermissionChecker>
					)}

					{/* Export — only on Reliefs tab */}
					{activeTab === "reliefs" && (
						<button
							onClick={() => setOpenExportModal(true)}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<BsDownload className="mr-2 text-[18px] text-gray-500" />
							Export Custom Tax Reliefs
						</button>
					)}

					{/* Add Dropdown */}
					<PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
						<div className="relative" ref={addDropdownRef}>
							<button
								onClick={() => setIsAddOpen(!isAddOpen)}
								className="bg-primary-mango text-white rounded-lg text-[12px] py-3 px-3 flex items-center gap-2 hover:bg-orange-600 transition-colors"
							>
								<AiOutlinePlus className="text-lg" />
								<span>Custom Tax Relief</span>
								<ChevronDown
									className={`w-4 h-4 transition-transform duration-200 ${
										isAddOpen ? "rotate-180" : ""
									}`}
								/>
							</button>

							{isAddOpen && (
								<div className="absolute top-full right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-50">
									<div className="p-2 space-y-1">
										<button
											onClick={() => {
												setIsAddOpen(false);
												setIsEditForm(false);
												setOpenGroupModal(true);
											}}
											className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 rounded-lg transition-colors hover:bg-gray-50"
										>
											<AiOutlinePlus className="w-5 h-5 text-gray-500 flex-shrink-0" />
											<span>Add Custom Tax Relief Group</span>
										</button>
										<button
											onClick={() => {
												setIsAddOpen(false);
												setIsEditForm(false);
												setOpenReliefModal(true);
											}}
											className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 rounded-lg transition-colors hover:bg-gray-50"
										>
											<AiOutlinePlus className="w-5 h-5 text-gray-500 flex-shrink-0" />
											<span>Add Employee Custom Tax Relief</span>
										</button>
									</div>
								</div>
							)}
						</div>
					</PermissionChecker>
				</div>
			</div>

			{/* Tabs */}
			<div className="flex border-b border-gray-200 mb-6">
				<button
					onClick={() => handleTabChange("groups")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "groups"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Tax Relief Groups
				</button>
				<button
					onClick={() => handleTabChange("reliefs")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "reliefs"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Employee Custom Tax Reliefs
				</button>
			</div>

			{/* Tables */}
			{activeTab === "groups" ? (
				<TanStackEmployeeTable
					ref={groupTableAPIRef}
					tableId={TableId.CUSTOM_TAX_RELIEF_GROUPS}
					data={groupTableData}
					columns={customTaxReliefGroupColumns}
					columnVisibility={groupColVisibility}
					onColumnVisibilityChange={setGroupColVisibility}
					loading={groupLoading}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.currentTarget);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={customTaxReliefGroupColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={groupPageCount}
					apiRef={groupTableAPIRef}
				/>
			) : (
				<TanStackEmployeeTable
					ref={reliefTableAPIRef}
					tableId={TableId.CUSTOM_TAX_RELIEFS}
					data={reliefTableData}
					columns={customTaxReliefColumns}
					columnVisibility={reliefColVisibility}
					onColumnVisibilityChange={setReliefColVisibility}
					loading={reliefLoading}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.currentTarget);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={customTaxReliefColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={reliefPageCount}
					apiRef={reliefTableAPIRef}
				/>
			)}

			{/* Action Popover */}
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={
					activeTab === "groups" ? setOpenGroupModal : setOpenReliefModal
				}
			/>

			{/* Delete Modal */}
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>

			{/* Group Modal (Add / Edit) */}
			{openGroupModal && (
				<CustomTaxReliefGroupModalForm
					open={openGroupModal}
					setOpen={setOpenGroupModal}
					title={
						isEditForm
							? "Update Custom Tax Relief Group"
							: "Add New Custom Tax Relief Group"
					}
					isEditForm={isEditForm}
					tableAPIRef={groupTableAPIRef}
					reloadTableData={reloadTableData}
				/>
			)}

			{/* Relief Modal (Add / Edit) */}
			{openReliefModal && (
				<CustomTaxReliefModalForm
					open={openReliefModal}
					setOpen={setOpenReliefModal}
					title={
						isEditForm
							? "Update Custom Tax Relief"
							: "Add New Custom Tax Relief"
					}
					isEditForm={isEditForm}
					tableAPIRef={reliefTableAPIRef}
					reloadTableData={reloadTableData}
					employees={employees?.data || []}
					employeeBands={employeeBands?.data || []}
					customTaxReliefGroups={customTaxReliefGroupsList?.data || []}
				/>
			)}

			{/* Upload Modal */}
			{openUploadModal && (
				<UploadCustomTaxRelief
					open={openUploadModal}
					setOpen={setOpenUploadModal}
				/>
			)}

			{/* Export Modal */}
			{openExportModal && currentCompany?.id && (
				<ExportCustomTaxReliefModal
					open={openExportModal}
					setOpen={setOpenExportModal}
					companyId={currentCompany.id}
				/>
			)}
		</div>
	);
};

export default CustomTaxReliefs;